/**
 * Eigenbau: Mono-Mikrolabel des Entwurfs — 505 Stellen, kein Tremor-Gegenstück.
 *
 * Der häufigste Baustein des ganzen Entwurfs. Im Bestand
 * (`vendor/tremor/components/`) gibt es nichts Vergleichbares: Tremors
 * `Label` ist ein Formularbeschrifter mit `htmlFor` und Deaktivierungszustand,
 * dies hier ist eine Auszeichnung — Rubrik, Kennung, Zähler, Datum.
 *
 * Warum Mono für Beschriftungen: Der Entwurf trennt Sagen von Zeigen. Fließtext
 * und Titel laufen in Plex Sans, alles Registerhafte in Plex Mono. Das macht
 * eine Zeile ohne Linien lesbar — die Schriftart übernimmt, was sonst ein
 * Rahmen leisten müsste.
 *
 * Die Grade sind die des Entwurfs (11, 11.5, 12, 12.5). Sie liegen dicht
 * beieinander, weil sie verschiedene Aufgaben haben und nicht verschiedene
 * Wichtigkeiten: 11 zählt, 11.5 steht in Zeilen, 12 überschreibt, 12.5 führt
 * eine Rubrik.
 */

import type { ReactNode } from "react";
import { cx } from "@/lib/utils";

const GRADE = {
  10: "text-[10px]",
  10.5: "text-[10.5px]",
  11: "text-[11px]",
  11.5: "text-[11.5px]",
  12: "text-[12px]",
  12.5: "text-[12.5px]",
} as const;

const TOENE = {
  /** Vorgabe: die stille Stufe für Beschriftungen neben dem Inhalt. */
  leise: "text-tinte4",
  /** Für Werte, die in einer Zeile mitlaufen. */
  mittel: "text-tinte3",
  /** Für Rubriken, die eine Gruppe führen. */
  laut: "text-tinte",
  /** Verlangt eine Entscheidung — überfällig, Aufhalter. */
  akzent: "text-akzent",
  /** Auf der Tafel, wo die Tinte umgekehrt läuft. */
  tafel: "text-tafel-tinte3",
  /** Gruppentitel der Schiene — die einzige türkisziehende Farbe im Haus. */
  rubrik: "text-tafel-rubrik",
} as const;

export function Mikrolabel({
  children,
  grad = 11.5,
  ton = "leise",
  zahl = false,
  betont = false,
  className,
  title,
  ariaLabel,
  testId,
}: {
  children: ReactNode;
  grad?: keyof typeof GRADE;
  ton?: keyof typeof TOENE;
  /**
   * Ziffern auf gleicher Breite. Ohne das zappeln Zähler beim Aktualisieren,
   * weil die `1` in Plex Mono zwar gleich breit ist wie die `8`, die
   * Vorgabeziffern einer Variable Font es aber nicht garantieren.
   */
  zahl?: boolean;
  betont?: boolean;
  className?: string;
  title?: string;
  ariaLabel?: string;
  testId?: string;
}) {
  return (
    <span
      title={title}
      aria-label={ariaLabel}
      data-testid={testId}
      className={cx(
        "font-mono",
        GRADE[grad],
        TOENE[ton],
        zahl && "tabular-nums",
        betont && "font-medium",
        className,
      )}
    >
      {children}
    </span>
  );
}
